import type { SignalResult } from "../scrapers/types";

const SIGNAL_ICONS: Record<string, string> = {
  posting_age: "🕐",
  vague_description: "📄",
  no_salary: "💰",
  high_applicant_anomaly: "👥",
  repost_detected: "🔄",
  requirements_mismatch: "⚡",
  company_legitimacy: "🏢",
  hiring_pattern: "📊",
  missing_contact: "📧",
};

const BACKGROUND_SIGNALS = new Set(["company_legitimacy", "hiring_pattern"]);

const SEVERITY_STYLES = {
  low: {
    color: "#22c55e",
    bg: "rgba(34,197,94,0.06)",
    border: "rgba(34,197,94,0.15)",
  },
  medium: {
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.06)",
    border: "rgba(245,158,11,0.15)",
  },
  high: {
    color: "#ff4444",
    bg: "rgba(255,68,68,0.06)",
    border: "rgba(255,68,68,0.15)",
  },
};

function SignalCard({ signal, index }: { signal: SignalResult; index: number }) {
  const sev =
    signal.severity === "high"
      ? SEVERITY_STYLES.high
      : signal.severity === "medium"
      ? SEVERITY_STYLES.medium
      : SEVERITY_STYLES.low;
  const pct = signal.maxScore > 0 ? (signal.score / signal.maxScore) * 100 : 0;
  const isBackground = BACKGROUND_SIGNALS.has(signal.id);

  return (
    <div
      className="p-3 sm:p-4 rounded-lg sm:rounded-xl animate-fade-in"
      style={{
        background: "var(--card)",
        border: `1px solid ${isBackground ? "rgba(129,140,248,0.1)" : "var(--border)"}`,
        animationDelay: `${index * 50}ms`,
        animationFillMode: "both",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5 sm:mb-3">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <span className="text-sm shrink-0">{SIGNAL_ICONS[signal.id] ?? "•"}</span>
          <span
            className="text-xs font-medium truncate"
            style={{ color: "var(--text-secondary)", fontFamily: "var(--font-body)" }}
          >
            {signal.label}
          </span>
        </div>
        <span
          className="px-1.5 sm:px-2 py-0.5 rounded-full shrink-0 ml-2"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.55rem",
            fontWeight: 500,
            color: sev.color,
            background: sev.bg,
            border: `1px solid ${sev.border}`,
          }}
        >
          {signal.severity}
        </span>
      </div>

      {/* Score bar */}
      <div className="mb-2.5 sm:mb-3">
        <div
          className="flex justify-between mb-1"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.55rem",
            color: "var(--text-tertiary)",
          }}
        >
          <span>signal</span>
          <span>
            {signal.score}/{signal.maxScore}
          </span>
        </div>
        <div
          className="h-1 rounded-full overflow-hidden"
          style={{ background: "rgba(255,255,255,0.04)" }}
        >
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              backgroundColor: sev.color,
              boxShadow: pct > 0 ? `0 0 8px ${sev.color}44` : "none",
            }}
          />
        </div>
      </div>

      <p
        className="text-xs leading-relaxed"
        style={{ color: "var(--text-tertiary)", fontSize: "0.7rem" }}
      >
        {signal.detail}
      </p>
    </div>
  );
}

interface SignalBreakdownProps {
  signals: SignalResult[];
}

export function SignalBreakdown({ signals }: SignalBreakdownProps) {
  const listingSignals = signals.filter((s) => !BACKGROUND_SIGNALS.has(s.id));
  const backgroundSignals = signals.filter((s) => BACKGROUND_SIGNALS.has(s.id));

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Listing signals */}
      <div>
        <div className="flex items-center gap-2 sm:gap-2.5 mb-2.5 sm:mb-3">
          <div
            className="w-1 h-3 sm:h-3.5 rounded-full"
            style={{ background: "var(--ghost-red)" }}
          />
          <span className="mono-label">Listing Analysis</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
          {listingSignals.map((signal, i) => (
            <SignalCard key={signal.id} signal={signal} index={i} />
          ))}
        </div>
      </div>

      {/* Background check signals */}
      {backgroundSignals.length > 0 && (
        <div>
          <div className="flex items-center gap-2 sm:gap-2.5 mb-2.5 sm:mb-3">
            <div
              className="w-1 h-3 sm:h-3.5 rounded-full"
              style={{ background: "var(--investigate)" }}
            />
            <span className="mono-label">Background Investigation</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
            {backgroundSignals.map((signal, i) => (
              <SignalCard
                key={signal.id}
                signal={signal}
                index={listingSignals.length + i}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
